import { useEffect, useState } from "react";

import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import type { Editor } from "tinymce";

import {
  type BlankField,
  listBlankFields,
  setBlankFieldText
} from "components/word-processor-field/blank-fields.utils";

// A key per field element, so an input stays with its blank while the list re-reads the form.
const fieldKeys = new WeakMap<HTMLElement, string>();
let nextFieldKey = 0;
const keyOf = (field: HTMLElement) => {
  let key = fieldKeys.get(field);
  if (!key) {
    nextFieldKey += 1;
    key = `fill-in-the-blank-${nextFieldKey}`;
    fieldKeys.set(field, key);
  }
  return key;
};

type FillInTheBlanksPanelProps = {
  /** The print dialog's editor once it is ready (WordProcessorField's onEditorInit); null before. */
  editor: Editor | null;
};

// ENG-2447: beside the generated form, every blank staff can type into (SN-19949's fields), by the
// name the converted form gave it, with a count of what is still empty. A view over the editor's own
// fields: typing here writes into the field the way typing in the form does, and typing in the form
// shows here. Nothing while the form holds no blank.
export default function FillInTheBlanksPanel({
  editor
}: FillInTheBlanksPanelProps) {
  const [fields, setFields] = useState<BlankField[]>([]);

  useEffect(() => {
    if (!editor) return undefined;
    // Preview unmounts the form and TinyMCE removes its editor, which the dialog still holds when
    // Edit Form brings this panel back: no body until the new editor's init replaces it.
    const refresh = () => {
      const body = editor.getBody();
      setFields(body ? listBlankFields(body) : []);
    };
    refresh();
    editor.on("input", refresh);
    editor.on("change", refresh);
    editor.on("SetContent", refresh);
    return () => {
      editor.off("input", refresh);
      editor.off("change", refresh);
      editor.off("SetContent", refresh);
    };
  }, [editor]);

  if (!editor || fields.length === 0) return null;
  const left = fields.filter((entry) => entry.isEmpty).length;

  return (
    <Stack
      component="section"
      aria-label="Fill in the blanks"
      spacing={1.5}
      sx={{
        width: 300,
        flexShrink: 0,
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        p: 1.5,
        maxHeight: 640,
        overflowY: "auto"
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography fontWeight={700}>Fill in the blanks</Typography>
        <Chip
          size="small"
          label={`${left} left`}
          // The theme flattens every filled chip to lilac; the colour is set here on purpose.
          sx={{
            bgcolor: left > 0 ? "warning.main" : "success.main",
            color: "common.white",
            fontWeight: 700
          }}
        />
      </Stack>
      {fields.map((entry, index) => (
        <TextField
          key={keyOf(entry.field)}
          id={keyOf(entry.field)}
          size="small"
          label={entry.label || `Blank ${index + 1}`}
          helperText={entry.context || undefined}
          value={entry.text}
          placeholder="Type here"
          onChange={(event) => {
            setBlankFieldText(editor, entry.field, event.target.value);
            setFields(listBlankFields(editor.getBody()));
          }}
          inputProps={{ style: { fontSize: 16 } }}
          InputLabelProps={{ shrink: true }}
        />
      ))}
      <Typography variant="body2" color="text.secondary">
        Blanks you leave empty print as lines.
      </Typography>
    </Stack>
  );
}
